/**
 * El código de la gestoría que trajo a alguien — `OD-0063` D-08 (ACCEPTED
 * 2026-10-25), «EL ENLACE DE ALTA».
 *
 * ## Por qué existe
 *
 * La gestoría manda a sus clientes un enlace `/registro?asesoria=<codigo>`. El
 * código tiene que llegar a la API **al crear la empresa**, que es el único
 * momento en que se atribuye (D-08: primer contacto, y nunca después). Pero
 * entre el enlace y ese momento hay un correo de confirmación que se abre en
 * OTRA pestaña, igual que con el plan elegido (`plan-elegido.ts`): sin
 * recordarlo, el código se perdería justo antes de usarse.
 *
 * ## Lo que esto NO es
 *
 * **No da acceso a nada.** El código viaja con el alta y la API decide si
 * atribuye: si no existe, si la gestoría está suspendida o si el programa
 * está apagado, no se atribuye y la empresa se crea igual. Dar acceso a la
 * gestoría es una instrucción del cliente (D-11), que se le pregunta DESPUÉS,
 * con un botón, sin nada marcado de antemano.
 *
 * **No valida el código.** Sólo comprueba que tiene la forma —ocho letras o
 * cifras, escrito como lo escriba una persona—, para no guardar en el
 * navegador lo que no puede ser un código. Cuál existe lo sabe la API
 * (`normalizarCodigo` y el perfil de la gestoría), y el test comprueba que
 * esta forma nunca descarta un código que la API aceptaría.
 *
 * **No es un dato fiscal** (`ADR-0004` X3) ni personal: es el código público
 * que la gestoría reparte. Si el navegador lo pierde —modo privado,
 * almacenamiento bloqueado— la empresa se crea sin atribuir, y el cliente
 * puede dar acceso igual con el código en Configuración.
 */

/** El parámetro del enlace de la gestoría: `/registro?asesoria=ABCD2345`. */
export const PARAMETRO = "asesoria";

const CLAVE = "faktelio.asesoria-del-enlace";

/** Una semana, como el plan elegido y el enlace de confirmación (`OD-0027`). */
export const CADUCA_MS = 7 * 24 * 60 * 60 * 1000;

/** Como lo escribe una persona: minúsculas, guiones y espacios no cuentan. */
export function formaDeCodigo(valor: unknown): string | null {
  if (typeof valor !== "string" || valor.length > 32) return null;
  const codigo = valor.toUpperCase().replace(/[\s-]/g, "");
  return /^[0-9A-Z]{8}$/.test(codigo) ? codigo : null;
}

/** El código que trae la URL, o `null`. */
export function codigoDelEnlace(search: string): string | null {
  return formaDeCodigo(new URLSearchParams(search).get(PARAMETRO));
}

function almacen(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function recordarAsesoria(codigo: string, ahora: number): void {
  try {
    almacen()?.setItem(CLAVE, JSON.stringify({ codigo, en: ahora }));
  } catch {
    // Sin almacenamiento se sigue: esta pestaña ya lo sabe por la URL.
  }
}

/** El código de una visita anterior, si sigue vigente. */
export function asesoriaPendiente(ahora: number): string | null {
  let crudo: string | null = null;
  try {
    crudo = almacen()?.getItem(CLAVE) ?? null;
  } catch {
    return null;
  }
  if (crudo === null) return null;
  try {
    const v = JSON.parse(crudo) as { codigo?: unknown; en?: unknown };
    // Lo guardado se vuelve a pasar por la MISMA forma: el navegador es del
    // usuario, y lo que haya ahí puede haberlo escrito cualquiera.
    const codigo = formaDeCodigo(v.codigo);
    const edad = typeof v.en === "number" ? ahora - v.en : -1;
    if (codigo !== null && edad >= 0 && edad < CADUCA_MS) return codigo;
  } catch {
    // Ilegible: se trata como si no hubiera nada, y se borra.
  }
  olvidarAsesoria();
  return null;
}

/** Se olvida cuando el cliente contesta a «¿le das acceso?», sea lo que sea. */
export function olvidarAsesoria(): void {
  try {
    almacen()?.removeItem(CLAVE);
  } catch {
    // Nada que hacer: caduca solo.
  }
}
